import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
    alpha,
    Button,
    Checkbox,
    createStyles,
    IconButton,
    InputAdornment,
    makeStyles,
    Paper,
    TextField,
    Theme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import i18n from 'i18n-js';
import clsx from 'clsx';
import { RestaurantGroup } from 'api/models';
import commonStyles from 'config/commonStyles';
import { SelectionMap } from '../interfaces';
import { truncateString } from 'helpers/helperFunctions';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        ...commonStyles,
        selectContainer: {
            position: 'relative',
            userSelect: 'none',
        },
        selectDropdown: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 300,
            overflowY: 'auto',
            zIndex: 10,
            marginTop: 0,
            borderRadius: 4,
            boxShadow: `0px 4px 10px ${theme.palette.divider}`,
        },
        dropdownItem: {
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
        },
        textEllipsis: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            paddingRight: 10,
        },
        actionsContainer: {
            display: 'flex',
            gap: 8,
            padding: '12px 12px',
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 2,
        },
        groupItem: {
            display: 'flex',
            alignItems: 'center',
            background: theme.palette.grey[200],
        },
    })
);

type Props = {
    style?: React.CSSProperties;
    groups: RestaurantGroup[];
    value?: SelectionMap;
    onChange?: (value: SelectionMap) => void;
};

function StockRestaurantSelect({ style, groups, value, onChange }: Props) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [internalMap, setInternalMap] = useState<SelectionMap>(new Map());

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectedMap = value ?? internalMap;
    const filteredGroups = filterGroups();

    const labelText = useMemo(() => {
        if (!selectedMap.size) {
            return i18n.t('common.selectFromTheList');
        }

        const restaurantMap = new Map(
            groups.flatMap((g) => g.restaurants).map((r) => [r.id, r.name])
        );

        const result: string[] = [];

        selectedMap.forEach((set) => {
            set.forEach((id) => {
                const name = restaurantMap.get(id);
                if (name) result.push(name);
            });
        });

        const text = result.join(' / ');
        return text ? truncateString(text, 30) : i18n.t('common.selectFromTheList');
    }, [selectedMap, groups]);

    function makeKey(groupId: string, restaurantId: string) {
        return `${groupId}_${restaurantId}_${Math.random().toString(16).substring(2)}`;
    }

    function updateMap(next: SelectionMap) {
        if (!value) {
            setInternalMap(next);
        }
        onChange?.(next);
    }

    function toggleRestaurant(groupId: string, restaurantId: string) {
        const next = new Map(selectedMap);
        const set = new Set(next.get(groupId) ?? []);
        if (set.has(restaurantId)) {
            set.delete(restaurantId);
        } else {
            set.add(restaurantId);
        }

        if (set.size === 0) {
            next.delete(groupId);
        } else {
            next.set(groupId, set);
        }

        updateMap(next);
    }

    function toggleGroup(groupId: string) {
        const group = groups.find((g) => g.id === groupId);
        if (!group) {
            updateMap(new Map());
            return;
        }
        const ids = group.restaurants.map((r) => r.id);
        const current = new Set(selectedMap.get(group.id) ?? []);
        const allSelected = ids.every((id) => current.has(id));

        const next = new Map(selectedMap);

        if (allSelected) {
            next.delete(group.id);
        } else {
            next.set(group.id, new Set(ids));
        }

        updateMap(next);
    }

    function getGroupState(groupId: string) {
        const group = groups.find((g) => g.id === groupId);
        if (!group) return { checked: false, indeterminate: false };

        const selected = selectedMap.get(group.id)?.size ?? 0;
        const total = group.restaurants.length;

        return {
            checked: selected === total && total > 0,
            indeterminate: selected > 0 && selected < total,
        };
    }

    function getRestaurantState(groupId: string, restaurantId: string) {
        const selected = selectedMap.get(groupId)?.has(restaurantId) ?? false;

        return {
            checked: selected,
            indeterminate: undefined,
        };
    }

    function selectAll() {
        const next = new Map<string, Set<string>>();
        groups.forEach((group) => {
            next.set(group.id, new Set(group.restaurants.map((r) => r.id)));
        });

        updateMap(next);
    }

    function clearAll() {
        updateMap(new Map());
    }

    function filterGroups() {
        if (!query.trim()) return groups;

        const q = query.toLowerCase();

        return groups
            .map((g) => {
                const filteredRestaurants = g.restaurants.filter((r) =>
                    r.name.toLowerCase().includes(q)
                );

                if (g.name.toLowerCase().includes(q) || filteredRestaurants.length > 0) {
                    return {
                        ...g,
                        restaurants: filteredRestaurants,
                    };
                }

                return null;
            })
            .filter(Boolean) as RestaurantGroup[];
    }

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div ref={containerRef} className={classes.selectContainer} style={style}>
            {/* Input trigger */}
            <TextField
                inputRef={inputRef}
                value={query}
                label={labelText}
                variant="filled"
                size="small"
                fullWidth
                onFocus={() => setOpen(true)}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {/* Clear button */}
                            {query.length > 0 && (
                                <IconButton
                                    size="small"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        setQuery('');
                                        setOpen(true);
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}

                            {/* Arrow toggle indicator */}
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setOpen((prev) => !prev)}
                            >
                                {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            {/* Dropdown */}
            {open && (
                <Paper className={classes.selectDropdown}>
                    {filteredGroups.length > 0 && (
                        <div className={classes.actionsContainer}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={selectAll}
                            >
                                {i18n.t('common.selectAll')}
                            </Button>

                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={clearAll}
                            >
                                {i18n.t('orders.clear')}
                            </Button>
                        </div>
                    )}

                    {/* Groups */}
                    {filteredGroups.map((group) => {
                        const state = getGroupState(group.id);

                        return (
                            <div key={group.id}>
                                {/* Group */}
                                <div
                                    className={clsx(classes.groupItem, classes.dropdownItem)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => toggleGroup(group.id)}
                                >
                                    <Checkbox
                                        checked={state.checked}
                                        indeterminate={state.indeterminate}
                                    />
                                    <strong className={classes.textEllipsis}>{group.name}</strong>
                                </div>

                                {/* Restaurants */}
                                {group.restaurants.map((r) => {
                                    const restaurantState = getRestaurantState(group.id, r.id);

                                    return (
                                        <div
                                            key={makeKey(group.id, r.id)}
                                            style={{ paddingLeft: 16 }}
                                            className={classes.dropdownItem}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => toggleRestaurant(group.id, r.id)}
                                        >
                                            <div className={classes.flexRow}>
                                                <Checkbox
                                                    checked={restaurantState.checked}
                                                    indeterminate={restaurantState.indeterminate}
                                                />
                                                <span className={classes.textEllipsis}>
                                                    {r.name}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </Paper>
            )}
        </div>
    );
}

export default StockRestaurantSelect;
